import { distributorCustomerInformationAtom, distributorRecordsAtom } from '@/core/store/distributor.store'
import {
    TCommissionResponseRow,
    TCommissionSortKey,
    commissionDataAtom,
    commissionErrorAtom,
    commissionHasFetchedAtom,
    commissionIsLoadingAtom,
    commissionLastBaidAtom
} from '@/core/store/commission.store'
import { getCommission } from '@/core/service/service'
import {
    Button,
    CircularProgress,
    IconButton,
    Paper,
    Stack,
    SvgIcon,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material'
import { useAtom, useAtomValue } from 'jotai'
import { useEffect, useMemo, useState } from 'react'
import { CUSTOMER_INFORMATION_KEY, DATA_DISTRIBUTOR_KEY } from '@/core/constants/auth'

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

type CommissionResponseItem = Record<string, unknown>

const parseNumber = (value: unknown): number => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value
    }

    if (typeof value === 'string') {
        const cleaned = value.replace(/[^0-9.,-]/g, '').replace(/,/g, '')
        const parsed = Number(cleaned)
        return Number.isFinite(parsed) ? parsed : 0
    }

    return 0
}

const formatNumber = (value: number, currency?: string) => {
    const formatted = Number.isFinite(value) ? value.toLocaleString('vi-VN') : '0'
    return currency ? `${formatted} ${currency}` : formatted
}

const getPeriodTimestamp = (period: string) => {
    if (!period) {
        return 0
    }

    const parsed = new Date(`${period}-01T00:00:00`)
    const timestamp = parsed.getTime()

    return Number.isNaN(timestamp) ? 0 : timestamp
}

const TABLE_COLUMNS: Array<{ label: string; sortKey?: TCommissionSortKey }> = [
    { label: 'Tháng hoa hồng', sortKey: 'period' },
    { label: 'Tổng thu nhập', sortKey: 'earningsFormatted' },
    { label: 'Thuế TNCN', sortKey: 'withheldFormatted' },
    { label: 'Thu nhập', sortKey: 'paidAmountFormatted' },
    { label: 'Tải về' }
]

const extractCommissionItems = (raw: unknown): CommissionResponseItem[] => {
    if (Array.isArray(raw)) {
        return raw as CommissionResponseItem[]
    }

    if (raw && typeof raw === 'object') {
        const container = raw as Record<string, unknown>
        const candidates = ['data', 'commissions', 'result', 'items', 'rows']

        for (const key of candidates) {
            const value = container[key]
            if (Array.isArray(value)) {
                return value as CommissionResponseItem[]
            }
            if (value && typeof value === 'object') {
                const nested = extractCommissionItems(value)
                if (nested.length > 0) {
                    return nested
                }
            }
        }
    }

    return []
}

const CommissionLayout = () => {
    const [pageSize, setPageSize] = useState<number>(25)
    const [page, setPage] = useState<number>(0)
    const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc')
    const [sortKey, setSortKey] = useState<TCommissionSortKey>('period')
    const [searchTerm, setSearchTerm] = useState<string>('')

    const distributorRecords = useAtomValue(distributorRecordsAtom)
    const distributorCustomerInformation = useAtomValue(distributorCustomerInformationAtom)

    const [commissionData, setCommissionData] = useAtom(commissionDataAtom)
    const [isCommissionLoading, setCommissionLoading] = useAtom(commissionIsLoadingAtom)
    const [commissionError, setCommissionError] = useAtom(commissionErrorAtom)
    const [hasFetched, setHasFetched] = useAtom(commissionHasFetchedAtom)
    const [lastFetchedBaid, setLastFetchedBaid] = useAtom(commissionLastBaidAtom)

    const currentBaid = JSON.parse(sessionStorage.getItem(DATA_DISTRIBUTOR_KEY) ?? '{}').baid ?? ''
    const currentName = JSON.parse(sessionStorage.getItem(DATA_DISTRIBUTOR_KEY) ?? '{}').name ?? ''
    const cstoken = JSON.parse(sessionStorage.getItem(CUSTOMER_INFORMATION_KEY) ?? '{}').token ?? ''
    const customerHref = JSON.parse(sessionStorage.getItem(CUSTOMER_INFORMATION_KEY) ?? '{}').href ?? ''

    useEffect(() => {
        if (!currentBaid) {
            setCommissionLoading(false)
            setCommissionData([])
            setCommissionError('')
            setHasFetched(false)
            setLastFetchedBaid(null)
            return
        }

        if (!cstoken || !customerHref) {
            setCommissionLoading(false)
            return
        }
        
        const fetchCommission = async () => {
            setCommissionLoading(true)
            setCommissionError('')

            try {
                const response = await getCommission({
                    token: cstoken,
                    href: customerHref
                })

                const rawItems = response?.data.data

                if (rawItems.length === 0) {
                    setCommissionError('Không có dữ liệu hoa hồng')
                }

                setCommissionData(rawItems)
                setHasFetched(true)
                setLastFetchedBaid(currentBaid)
            } catch (error) {
                setCommissionData([])
                setCommissionError('Khôngสามารถดึงข้อมูล hoa hồng')
            } finally {
                setCommissionLoading(false)
            }
        }

        fetchCommission()
    }, [])

    const filteredData = useMemo(() => {
        const search = searchTerm.trim().toLowerCase()

        const filtered = commissionData.filter((record: TCommissionResponseRow) => {
            if (!search) {
                return true
            }

            return (
                record.period.toLowerCase().includes(search) ||
                record.earningsFormatted.includes(search) ||
                record.withheldFormatted.includes(search) ||
                record.paidAmountFormatted.includes(search)
            )
        })

        const sorter = [...filtered].sort((a: TCommissionResponseRow, b: TCommissionResponseRow) => {
            const getComparableValue = (record: TCommissionResponseRow) => {
                switch (sortKey) {
                    case 'earningsFormatted':
                        return record.earningsFormatted
                    case 'withheldFormatted':
                        return record.withheldFormatted
                    case 'paidAmountFormatted':
                        return record.paidAmountFormatted
                    case 'period':
                    default:
                        return getPeriodTimestamp(record.period)
                }
            }

            const valueA = getComparableValue(a)
            const valueB = getComparableValue(b)

            if (valueA === valueB) return 0

            const comparison = valueA < valueB ? -1 : 1

            return sortOrder === 'desc' ? -comparison : comparison
        })

        return sorter
    }, [commissionData, searchTerm, sortKey, sortOrder])

    const totalResults = filteredData.length
    const totalPages = Math.max(1, Math.ceil(totalResults / pageSize))
    const currentPage = Math.min(page, totalPages - 1)
    const start = currentPage * pageSize
    const end = Math.min(start + pageSize, totalResults)
    const currentRows = filteredData.slice(start, end)

    const handleChangePageSize = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPageSize(Number(event.target.value))
        setPage(0)
    }

    const handlePrevPage = () => {
        setPage(prev => Math.max(prev - 1, 0))
    }

    const handleNextPage = () => {
        setPage(prev => Math.min(prev + 1, totalPages - 1))
    }

    const handleGoToPage = (targetPage: number) => {
        setPage(Math.min(Math.max(targetPage, 0), totalPages - 1))
    }

    const handleGoToFirstPage = () => {
        setPage(0)
    }

    const handleGoToLastPage = () => {
        setPage(totalPages - 1)
    }

    const visiblePages = useMemo(() => {
        const maxVisible = 5

        if (totalPages <= maxVisible) {
            return Array.from({ length: totalPages }, (_, index) => index)
        }

        let startPage = Math.max(currentPage - Math.floor(maxVisible / 2), 0)
        let endPage = startPage + maxVisible - 1

        if (endPage >= totalPages) {
            endPage = totalPages - 1
            startPage = endPage - (maxVisible - 1)
        }

        return Array.from({ length: maxVisible }, (_, index) => startPage + index)
    }, [currentPage, totalPages])

    const handleSortToggle = (key: TCommissionSortKey) => {
        if (sortKey === key) {
            setSortOrder(prev => (prev === 'desc' ? 'asc' : 'desc'))
        } else {
            setSortKey(key)
            setSortOrder('desc')
        }
    }

    const getSortIconPath = (key: TCommissionSortKey) => {
        if (sortKey !== key) {
            return 'M10 5l-3 3h2v4h2V8h2l-3-3zm4 6v4h-2l3 3 3-3h-2v-4h-2z'
        }

        return sortOrder === 'desc'
            ? 'M10 5l-3 3h2v4h2V8h2l-3-3zm4 6v4h-2l3 3 3-3h-2v-4h-2z'
            : 'M14 7l3 3h-2v4h-2V10h-2l3-3zm-4 6v4h2l-3 3-3-3h2v-4h2z'
    }

    const columnCount = TABLE_COLUMNS.length

    const handleOpenDownload = (downloadUrl: string) => {
        if (!downloadUrl) {
            return
        }

        console.log('downloadUrl', downloadUrl)

        if (typeof window === 'undefined') {
            return
        }

        const newWindow = window.open(downloadUrl, '_blank', 'noopener,noreferrer')
        if (newWindow) {
            newWindow.opener = null
        }
    }

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 3,
                boxShadow: '0px 8px 24px rgba(15,15,15,0.06)'
            }}>
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
                spacing={2}
                mb={3}>
                <Typography variant="h6" fontWeight={700} color="text.primary">
                    Danh sách hoa hồng {currentBaid ? `(${currentBaid}, ${currentName})` : ''}
                </Typography>
            </Stack>

            <Stack direction="row" spacing={2} justifyContent="space-between" flexWrap="wrap" mb={3}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography variant="body2" color="black" fontWeight={600}>
                        Thể hiện
                    </Typography>
                    <TextField
                        select
                        size="small"
                        value={pageSize}
                        onChange={handleChangePageSize}
                        sx={{ minWidth: 80 }}
                        SelectProps={{ native: true }}>
                        {PAGE_SIZE_OPTIONS.map(option => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </TextField>
                    <Typography variant="body2" color="black" fontWeight={600}>
                        kết quả
                    </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" color="black" fontWeight={600}>
                        tìm kiếm :
                    </Typography>
                    <TextField
                        size="small"
                        sx={{ minWidth: 200 }}
                        value={searchTerm}
                        onChange={event => {
                            setSearchTerm(event.target.value)
                            setPage(0)
                        }}
                    />
                </Stack>
            </Stack>

            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                <Table
                    sx={{
                        '& .MuiTableCell-root': {
                            borderBottom: '1px solid #e2e8f0',
                            textAlign: 'center'
                        }
                    }}
                    size="medium">
                    <TableHead
                        sx={{
                            '& .MuiTableCell-root': {
                                padding: '10px 16px'
                            }
                        }}>
                        <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                            {TABLE_COLUMNS.map(({ label, sortKey: columnSortKey }, index) => (
                                <TableCell key={label} sx={{ fontWeight: 600, borderRight: index === columnCount - 1 ? 'none' : '1px solid #dee2e6' }}>
                                    <Stack width="100%" direction="row" justifyContent="center" alignItems="center">
                                        <Typography fontWeight={600}>{label}</Typography>

                                        {columnSortKey && (
                                            <IconButton
                                                onClick={() => handleSortToggle(columnSortKey)}
                                                sx={{
                                                    background: 'transparent',
                                                    padding: '0px 0px 0px 10px',
                                                    '&:hover': { backgroundColor: 'transparent' },
                                                    color: sortKey === columnSortKey ? 'text.primary' : 'text.disabled'
                                                }}>
                                                <SvgIcon sx={{ fontSize: 32, padding: 0 }}>
                                                    <path d={getSortIconPath(columnSortKey)} />
                                                </SvgIcon>
                                            </IconButton>
                                        )}
                                    </Stack>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isCommissionLoading ? (
                            <TableRow>
                                <TableCell colSpan={columnCount} sx={{ py: 6, textAlign: 'center' }}>
                                    <CircularProgress size={28} />
                                </TableCell>
                            </TableRow>
                        ) : commissionError ? (
                            <TableRow sx={{ backgroundColor: 'rgba(0,0,0,0.04)' }}>
                                <TableCell colSpan={columnCount} sx={{ py: 4, color: 'error.main', textAlign: 'center', fontWeight: 600 }}>
                                    {commissionError}
                                </TableCell>
                            </TableRow>
                        ) : currentRows.length === 0 ? (
                            <TableRow sx={{ backgroundColor: 'rgba(0,0,0,0.04)' }}>
                                <TableCell colSpan={columnCount} sx={{ py: 4, color: 'text.secondary' }}>
                                    Không có dữ liệu
                                </TableCell>
                            </TableRow>
                        ) : (
                            currentRows.map((record, index) => (
                                <TableRow key={`${record.period}-${index}`} sx={{ backgroundColor: index % 2 === 0 ? 'rgba(0,0,0,0.04)' : '#FFF' }}>
                                    <TableCell sx={{ borderRight: '1px solid #dee2e6' }}>{record.period}</TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #dee2e6' }}>{record.earningsFormatted}</TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #dee2e6' }}>{record.withheldFormatted}</TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #dee2e6' }}>{record.paidAmountFormatted}</TableCell>
                                    <TableCell>
                                        {record.downloadUrl ? (
                                            <Button
                                                variant="contained"
                                                size="medium"
                                                sx={{ textTransform: 'capitalize', background: '#007bff', color: '#FFF' }}
                                                onClick={() => handleOpenDownload(record.downloadUrl)}>
                                                tải về
                                            </Button>
                                        ) : (
                                            <Button
                                                variant="contained"
                                                size="medium"
                                                sx={{ textTransform: 'capitalize', background: '#b0bec5', color: '#FFF' }}
                                                disabled>
                                                tải về
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems="center" mt={2} gap={2}>
                <Typography variant="body2" color="black">
                    Thể hiện {totalResults === 0 ? 0 : start + 1} đến {end} của {totalResults} kết quả
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Button variant="outlined" size="small" onClick={handleGoToFirstPage} disabled={currentPage === 0}>
                        « Đầu
                    </Button>
                    <Button variant="outlined" size="small" onClick={handlePrevPage} disabled={currentPage === 0}>
                        trở lại
                    </Button>

                    <Stack direction="row" spacing={0.5} alignItems="center">
                        {visiblePages.map(pageIndex => {
                            const lastVisiblePageIndex = visiblePages[visiblePages.length - 1] ?? 0
                            const isLastVisible = pageIndex === lastVisiblePageIndex

                            return (
                                <Stack key={pageIndex} direction="row" alignItems="center" spacing={0.5}>
                                    <Button
                                        variant={currentPage === pageIndex ? 'contained' : 'outlined'}
                                        size="small"
                                        onClick={() => handleGoToPage(pageIndex)}
                                        sx={{ minWidth: 40 }}>
                                        {pageIndex + 1}
                                    </Button>
                                    {isLastVisible && lastVisiblePageIndex < totalPages - 1 && (
                                        <Typography variant="body2" color="text.secondary" sx={{ px: 0.5 }}>
                                            ...
                                        </Typography>
                                    )}
                                </Stack>
                            )
                        })}
                    </Stack>

                    <Button variant="outlined" size="small" onClick={handleNextPage} disabled={currentPage >= totalPages - 1}>
                        tiếp
                    </Button>
                    <Button variant="outlined" size="small" onClick={handleGoToLastPage} disabled={currentPage >= totalPages - 1}>
                        Cuối »
                    </Button>
                </Stack>
            </Stack>
        </Paper>
    )
}

export default CommissionLayout

